import styled from 'styled-components';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Label,
} from 'recharts';

export type TrendRow = {
  label: string; // x축: ~07.06 등
  progress: number; // 진행건수 (Bar)
  wonCount: number; // 낙찰건수 (Bar)
  winRate: number; // 낙찰률(%)       (Line, 우측축: rate)
  winPriceRate: number; // 낙찰가율(%)     (Line, 우측축: rate)
  avgBidders: number; // 평균 응찰자 수(명) (Line, 우측축2: bidders)
};

type Props = { data?: TrendRow[] };

const H = 340; // 차트 높이
const LEFT_W = 50; // 좌측 축 폭
const RIGHT_W = 40; // 우측 축 폭(잘림 방지)
const TICK_W = 56; // X축 한 칸 폭(스크롤 필요시 조정)

// 커스텀 툴팁 컴포넌트
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;

  // 툴팁에 표시되는 항목 (실 데이터 가공)
  const items = [
    {
      key: 'progress',
      label: '진행건수',
      color: '#B9BFC9',
      value: row.progress ?? 0,
      valueFmt: (v: any) => Number(v).toLocaleString('ko-KR'),
      img: '/assets/images/icon/gray_box.svg',
    },
    {
      key: 'wonCount',
      label: '낙찰건수',
      color: '#424854',
      value: row.wonCount ?? 0,
      valueFmt: (v: any) => Number(v).toLocaleString('ko-KR'),
      img: '/assets/images/icon/dark_gray_box.svg',
    },
    {
      key: 'winRate',
      label: '낙찰률',
      color: '#0F9667',
      value: row.winRate ?? 0,
      valueFmt: (v: any) => `${Number(v).toFixed(1)}%`,
      img: '/assets/images/icon/green_dot.svg',
    },
    {
      key: 'winPriceRate',
      label: '낙찰가율',
      color: '#3F6EF1',
      value: row.winPriceRate ?? 0,
      valueFmt: (v: any) => `${Number(v).toFixed(1)}%`,
      img: '/assets/images/icon/blue_dot.svg',
    },
    {
      key: 'avgBidders',
      label: '평균 응찰자 수',
      color: '#F15171',
      value: row.avgBidders ?? 0,
      valueFmt: (v: any) => `${Number(v).toFixed(1)}명`,
      img: '/assets/images/icon/red_dot.svg',
    },
  ];

  // 진행건수=progress, 낙찰건수=wonCount, 낙찰률=winRate, 낙찰가율=winPriceRate, 평균 응찰자 수 = avgBidders
  return (
    <TooltipRoot>
      <TooltipLabel>{label}</TooltipLabel>
      <TooltipList>
        {items.map((item) => (
          <TooltipItem key={item.key}>
            <TooltipLegendWrapper>
              <img
                src={item.img}
                alt={item.label}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 8,
                  objectFit: 'cover',
                  marginRight: 4,
                }}
              />
              <TooltipLegendText>{item.label}</TooltipLegendText>
            </TooltipLegendWrapper>
            <TooltipValue>{item.valueFmt(item.value)}</TooltipValue>
          </TooltipItem>
        ))}
      </TooltipList>
    </TooltipRoot>
  );
}

export default function AuctionTrendsAnalysis({ data = [] }: Props) {
  // 스케일 계산
  const toNum = (v: any) => Number(v) || 0;
  const maxCount = Math.max(
    0,
    ...data.map((d) => Math.max(toNum(d.progress), toNum(d.wonCount))),
  );
  const maxRate = Math.max(
    0,
    ...data.map((d) => Math.max(d.winRate || 0, d.winPriceRate || 0)),
  );

  // 내부 스크롤 폭 (데이터 개수에 따라 넓힘)
  const innerWidth = Math.max(TICK_W * data.length, 480);

  const chartData = data.map((d) => {
    const p = toNum(d.progress);
    const w = Math.max(0, toNum(d.wonCount));
    const wonCountCapped = Math.min(w, p);
    const progressRemainder = Math.max(0, p - wonCountCapped);
    return { ...d, wonCountCapped, progressRemainder };
  });

  return (
    <Container>
      <GraphWrap>
        <AxisPane style={{ width: LEFT_W }}>
          <ResponsiveContainer width="100%" height={H}>
            <ComposedChart
              data={chartData}
              margin={{ top: 22, right: 10, left: -10, bottom: 44 }}
              syncId="auction"
            >
              <XAxis dataKey="label" hide />
              <YAxis
                yAxisId="count"
                domain={[0, maxCount]}
                tick={{ fill: '#787D87', fontSize: 12 }}
                tickLine={{ stroke: '#B9BFC9' }}
                axisLine={{ stroke: '#9AA0A6' }}
              >
                <Label
                  value="건수"
                  position="insideTopLeft"
                  offset={30}
                  dy={-50}
                  style={{ fill: '#787D87', fontSize: 12, fontWeight: 500 }}
                />
              </YAxis>
            </ComposedChart>
          </ResponsiveContainer>
        </AxisPane>
        <ScrollPane>
          <Inner style={{ width: innerWidth, height: H }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 12, right: 0, left: 0, bottom: 0 }}
                syncId="auction"
              >
                <XAxis
                  dataKey="label"
                  interval={0}
                  tickMargin={8}
                  tick={{ fill: '#787D87', fontSize: 12 }}
                  axisLine={{ stroke: '#9AA0A6' }}
                  tickLine={{ stroke: '#B9BFC9' }}
                />
                <YAxis yAxisId="count" domain={[0, maxCount]} hide />
                <YAxis yAxisId="rate" domain={[0, maxRate]} hide />
                <Tooltip
                  content={<CustomTooltip />}
                  wrapperStyle={{ fontSize: 12 }}
                  cursor={{ stroke: '#B9BFC9', strokeDasharray: '3 3' }}
                />
                {/* 스택 막대: 진행(라이트 그레이) + 낙찰(다크 그레이) */}
                <Bar
                  yAxisId="count"
                  dataKey="wonCountCapped"
                  name="낙찰건수"
                  stackId="cnt"
                  fill="#424854"
                  barSize={48}
                  radius={[0, 0, 8, 8]} // 아랫면 둥글림
                />
                <Bar
                  yAxisId="count"
                  dataKey="progressRemainder"
                  name="진행건수"
                  stackId="cnt"
                  fill="#D3D8E0"
                  barSize={48}
                  radius={[8, 8, 0, 0]} // 윗면 둥글림
                />
                {/* 라인: 낙찰가율(파랑), 낙찰률(초록) → 우측 축 */}
                <Line
                  yAxisId="rate"
                  type="linear"
                  dataKey="winPriceRate"
                  name="낙찰가율"
                  stroke="#3F6EF1"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#3F6EF1', strokeWidth: 1 }}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId="rate"
                  type="linear"
                  dataKey="winRate"
                  name="낙찰률"
                  stroke="#0F9667"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#0F9667', strokeWidth: 1 }}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId="rate"
                  type="linear"
                  dataKey="avgBidders"
                  name="평균 응찰자 수"
                  stroke="#F15171"
                  strokeWidth={2}
                  dot={{ r: 3, strokeWidth: 1, fill: '#F15171' }}
                  activeDot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Inner>
        </ScrollPane>
        <AxisPane style={{ width: RIGHT_W }}>
          <ResponsiveContainer width="100%" height={H}>
            <ComposedChart
              data={chartData}
              margin={{ top: 22, right: -20, left: 10, bottom: 44 }}
              syncId="auction"
            >
              <XAxis dataKey="label" hide />
              <YAxis
                yAxisId="rate"
                orientation="right"
                domain={[0, maxRate]}
                tick={{ fill: '#787D87', fontSize: 12 }}
                tickLine={{ stroke: '#B9BFC9' }}
                axisLine={{ stroke: '#B9BFC9' }}
              >
                <Label
                  value="비율"
                  position="insideTopRight"
                  offset={30}
                  dy={-50}
                  style={{ fill: '#787D87', fontSize: 12, fontWeight: 500 }}
                />
              </YAxis>
            </ComposedChart>
          </ResponsiveContainer>
        </AxisPane>
      </GraphWrap>
      <LabelRow>
        <DotProgress>
          <LabelMarker $color="#B9BFC9" />
          <LabelTag>진행건수</LabelTag>
        </DotProgress>
        <DotProgress>
          <LabelMarker $color="#424854" />
          <LabelTag>낙찰건수</LabelTag>
        </DotProgress>
        <DotProgress>
          <Progress>
            <ProgressBar $color="#0F9667" />
            <Dot $color="#0F9667" />
            <ProgressBar $color="#0F9667" />
          </Progress>
          <LabelTag>낙찰률</LabelTag>
        </DotProgress>
        <DotProgress $width="92px">
          <ProgressBar $color="#3F6EF1" />
          <Dot $color="#3F6EF1" />
          <ProgressBar $color="#3F6EF1" />
          <LabelTag>낙찰가률</LabelTag>
        </DotProgress>
        <DotProgress $width="120px">
          <ProgressBar $color="#F15171" />
          <Dot $color="#F15171" />
          <ProgressBar $color="#F15171" />
          <LabelTag>평균 응찰자 수</LabelTag>
        </DotProgress>
      </LabelRow>
    </Container>
  );
}

const TooltipRoot = styled.div`
  background: #fff;
  border: 1px solid #e4e6ea;
  border-radius: 8px;
  padding: 14px 12px 10px 12px;
  box-shadow: 0 8px 24px 0 rgba(56, 65, 82, 0.1);
  min-width: 200px;
`;

const TooltipLabel = styled.div`
  font-weight: 500;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 24px;
  letter-spacing: -2%;
  color: #424854;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
  white-space: nowrap;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TooltipItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
`;

const TooltipLegendWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TooltipLegendText = styled.div`
  color: #424854;
  font-size: 13px;
  width: 68px;
  white-space: nowrap;
`;

const TooltipValue = styled.div`
  font-size: 14px;
  color: #111322;
  font-weight: 500;
  white-space: nowrap;
`;

// ⬇️ 최상단 컨테이너에 루트 마크
const Container = styled.div.attrs<{ 'data-capture-root'?: string }>(() => ({
  'data-capture-root': '',
}))`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

// ⬇️ 가운데 스크롤 영역에 마크
const ScrollPane = styled.div.attrs<{ 'data-export-expand'?: string }>(() => ({
  'data-export-expand': '',
}))`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

// ⬇️ 내부 실제 너비가 들어가는 엘리먼트에도 마크
const Inner = styled.div.attrs<{ 'data-export-inner'?: string }>(() => ({
  'data-export-inner': '',
}))`
  padding: 0 0 8px 0;
`;

const GraphWrap = styled.div`
  display: grid;
  grid-template-columns: ${LEFT_W}px minmax(0, 1fr) ${RIGHT_W}px;
  align-items: start;
  background: #fff;
`;

const AxisPane = styled.div``;

const LabelRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: flex-start;
  align-self: stretch;
  flex-wrap: wrap;
`;

const DotProgress = styled.div<{ $width?: string }>`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 4px;
`;

const ProgressBar = styled.div<{ $color?: string }>`
  height: 2px;
  width: 6px;
  /* flex: 1; */
  background-color: ${({ $color }) => $color ?? '#0f9667'};
`;

const LabelMarker = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin: 4px;
  background-color: ${({ $color }) => $color};
`;

const Dot = styled.div<{ $color?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background-color: ${({ $color }) => $color ?? '#0f9667'};
`;

const LabelTag = styled.div`
  font-size: 13px;
  color: #424854;
  font-weight: 500;
`;

const Progress = styled.div`
  display: flex;
  gap: 1px;
  align-items: center;
`;
